import React, { useState, useRef, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Box, Typography, Avatar, CircularProgress, Alert, Chip, IconButton,
  Tooltip, Collapse,
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ImageIcon from '@mui/icons-material/Image';
import CloseIcon from '@mui/icons-material/Close';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { useAuth } from '../../contexts/AuthContext';
import { engineeringApi } from '../../services/api';
import { debugService } from '../../services/debugService';
import { extractApiError } from '../../types/apiError';
import { HubThemeScope } from '../clients/hub/designSystem';

// Severity → theme status tokens (no raw hex — the ratchet bans it in .tsx).
const SEV_TOKEN: Record<string, string> = {
  low: 'var(--status-on-track)',
  medium: 'var(--status-warning)',
  high: 'var(--status-off-track)',
  critical: 'var(--status-off-track)',
};

interface FeatureRequestDialogProps {
  open: boolean;
  onClose: () => void;
}

interface AttachmentFile {
  file: File;
  preview?: string;
}

const SEVERITY_OPTIONS = [
  { value: 'low', label: 'Low', hint: 'Minor inconvenience' },
  { value: 'medium', label: 'Medium', hint: 'Affects workflow' },
  { value: 'high', label: 'High', hint: 'Blocks work' },
  { value: 'critical', label: 'Critical', hint: 'System down / data loss' },
];

const MAX_FILES = 10;
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const FeatureRequestDialog: React.FC<FeatureRequestDialogProps> = ({ open, onClose }) => {
  const { user } = useAuth();
  const [requestType, setRequestType] = useState<'feature' | 'bug'>('feature');
  const [description, setDescription] = useState('');
  const [stepsToReproduce, setStepsToReproduce] = useState('');
  const [expectedBehavior, setExpectedBehavior] = useState('');
  const [actualBehavior, setActualBehavior] = useState('');
  const [pageUrl, setPageUrl] = useState('');
  const [severity, setSeverity] = useState('medium');
  const [attachments, setAttachments] = useState<AttachmentFile[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [snapping, setSnapping] = useState(false);

  const addFiles = useCallback((files: FileList | File[]) => {
    const newFiles: AttachmentFile[] = [];
    for (const file of Array.from(files)) {
      if (file.size > MAX_FILE_SIZE) {
        setError(`${file.name} is too large (max 10MB)`);
        continue;
      }
      const af: AttachmentFile = { file };
      if (file.type.startsWith('image/')) {
        af.preview = URL.createObjectURL(file);
      }
      newFiles.push(af);
    }
    setAttachments(prev => [...prev, ...newFiles].slice(0, MAX_FILES));
  }, []);

  const removeAttachment = (idx: number) => {
    setAttachments(prev => {
      const next = [...prev];
      if (next[idx].preview) URL.revokeObjectURL(next[idx].preview!);
      next.splice(idx, 1);
      return next;
    });
  };

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files: File[] = [];
    for (const item of Array.from(items)) {
      if (item.kind === 'file') {
        const f = item.getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length > 0) {
      e.preventDefault();
      addFiles(files);
    }
  }, [addFiles]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  }, [addFiles]);

  // Bug wizard: capture a one-shot screenshot of the page BEHIND this dialog and
  // attach it. Reuses the Debug Session Builder's html2canvas primitive; the
  // dialog is tagged data-debug-overlay so captureCanvasBlob's built-in ignore
  // list removes it from the shot (no timing hacks). The blob rides the SAME
  // attachment path as any uploaded file — zero change to the submit flow.
  const takeSnapshot = useCallback(async () => {
    setSnapping(true);
    setError(null);
    try {
      const blob = await debugService.captureCanvasBlob({ quality: 0.85 });
      if (!blob) { setError('Could not capture a snapshot of this page.'); return; }
      const file = new File([blob], `snapshot-${Date.now()}.jpg`, { type: 'image/jpeg' });
      // One snapshot per report: drop any prior auto-snapshot first.
      setAttachments((prev) => {
        prev.filter((a) => a.file.name.startsWith('snapshot-') && a.preview).forEach((a) => URL.revokeObjectURL(a.preview!));
        return prev.filter((a) => !a.file.name.startsWith('snapshot-'));
      });
      addFiles([file]);
    } catch {
      setError('Could not capture a snapshot of this page.');
    } finally {
      setSnapping(false);
    }
  }, [addFiles]);

  const handleSubmit = async () => {
    if (!description.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('request_type', requestType);
      formData.append('description', description.trim());
      if (requestType === 'bug') {
        if (stepsToReproduce.trim()) formData.append('steps_to_reproduce', stepsToReproduce.trim());
        if (expectedBehavior.trim()) formData.append('expected_behavior', expectedBehavior.trim());
        if (actualBehavior.trim()) formData.append('actual_behavior', actualBehavior.trim());
        if (pageUrl.trim()) formData.append('page_url', pageUrl.trim());
        formData.append('severity', severity);
      }
      for (const att of attachments) {
        formData.append('attachments', att.file);
      }
      await engineeringApi.createRequest(formData);
      setSuccess(true);
      setTimeout(() => handleClose(), 1800);
    } catch (err: any) {
      setError(extractApiError(err, 'Something went wrong. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    for (const a of attachments) { if (a.preview) URL.revokeObjectURL(a.preview); }
    setDescription('');
    setStepsToReproduce('');
    setExpectedBehavior('');
    setActualBehavior('');
    setPageUrl('');
    setSeverity('medium');
    setAttachments([]);
    setRequestType('feature');
    setSuccess(false);
    setError(null);
    onClose();
  };

  const isBug = requestType === 'bug';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: '6px' } }}
      slotProps={{ root: { 'data-debug-overlay': 'true' } } as any}>
      <HubThemeScope>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isBug ? <BugReportIcon sx={{ color: 'var(--status-off-track)' }} /> : <LightbulbIcon sx={{ color: 'var(--paprika)' }} />}
          <Typography sx={{ fontFamily: 'var(--font-display, "Inter Tight", Inter, sans-serif)', fontWeight: 700, fontSize: '20px' }}>Submit a Request</Typography>
        </Box>
      </DialogTitle>

      <DialogContent
        ref={dropRef}
        onPaste={handlePaste}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        sx={{
          position: 'relative',
          ...(dragOver && {
            '&::after': {
              content: '"Drop files here"',
              position: 'absolute', inset: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '2px dashed var(--indigo)', borderRadius: '4px',
              bgcolor: 'var(--indigo-soft)', color: 'var(--indigo-on-soft)',
              fontSize: '14px', fontWeight: 600, zIndex: 10, pointerEvents: 'none',
            },
          }),
        }}
      >
        {success ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 56, color: 'var(--status-on-track)', mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>Submitted!</Typography>
            <Typography variant="body2" color="text.secondary">
              Your {isBug ? 'bug report' : 'feature request'} has been logged. We'll review it shortly.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* Submitter info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Avatar src={user?.avatarUrl} sx={{ width: 32, height: 32, fontSize: '12.5px', bgcolor: 'primary.main' }}>
                {user?.displayName?.[0]?.toUpperCase() || '?'}
              </Avatar>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{user?.displayName || 'Unknown User'}</Typography>
                <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
              </Box>
            </Box>

            {/* Type selector */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip
                icon={<LightbulbIcon sx={{ fontSize: 16 }} />}
                label="Feature Request"
                variant={requestType === 'feature' ? 'filled' : 'outlined'}
                color={requestType === 'feature' ? 'primary' : 'default'}
                onClick={() => setRequestType('feature')}
                sx={{ flex: 1, justifyContent: 'center', cursor: 'pointer' }}
              />
              <Chip
                icon={<BugReportIcon sx={{ fontSize: 16 }} />}
                label="Bug Report"
                variant={requestType === 'bug' ? 'filled' : 'outlined'}
                color={requestType === 'bug' ? 'error' : 'default'}
                onClick={() => setRequestType('bug')}
                sx={{ flex: 1, justifyContent: 'center', cursor: 'pointer' }}
              />
            </Box>

            {/* Main description */}
            <TextField
              label={isBug ? 'What happened?' : "Describe the feature you'd like"}
              multiline minRows={3} maxRows={8} value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder={isBug
                ? 'Briefly describe what went wrong...'
                : 'What would you like to see? How would it help your workflow?'}
              fullWidth autoFocus
            />

            {/* Bug-specific fields */}
            <Collapse in={isBug}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Severity */}
                <Box>
                  <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Severity</Typography>
                  <Box sx={{ display: 'flex', gap: 0.75 }}>
                    {SEVERITY_OPTIONS.map(s => (
                      <Tooltip key={s.value} title={s.hint} arrow>
                        <Chip
                          icon={s.value === 'critical' ? <WarningAmberIcon sx={{ fontSize: 14 }} /> : undefined}
                          label={s.label}
                          size="small"
                          variant={severity === s.value ? 'filled' : 'outlined'}
                          onClick={() => setSeverity(s.value)}
                          sx={{
                            cursor: 'pointer', flex: 1, justifyContent: 'center', fontWeight: 600, borderRadius: '4px',
                            ...(severity === s.value
                              ? { bgcolor: SEV_TOKEN[s.value], color: 'var(--paper)', '&:hover': { bgcolor: SEV_TOKEN[s.value] } }
                              : { bgcolor: 'transparent', borderColor: SEV_TOKEN[s.value], color: SEV_TOKEN[s.value] }),
                          }}
                        />
                      </Tooltip>
                    ))}
                  </Box>
                </Box>

                <TextField
                  label="Steps to reproduce"
                  multiline minRows={2} maxRows={6} value={stepsToReproduce}
                  onChange={e => setStepsToReproduce(e.target.value)}
                  placeholder="1. Go to...\n2. Click on...\n3. See error..."
                  fullWidth size="small"
                />

                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <TextField
                    label="Expected behavior"
                    multiline minRows={2} maxRows={4} value={expectedBehavior}
                    onChange={e => setExpectedBehavior(e.target.value)}
                    placeholder="What should have happened?"
                    fullWidth size="small"
                  />
                  <TextField
                    label="Actual behavior"
                    multiline minRows={2} maxRows={4} value={actualBehavior}
                    onChange={e => setActualBehavior(e.target.value)}
                    placeholder="What actually happened?"
                    fullWidth size="small"
                  />
                </Box>

                <TextField
                  label="Page URL (where the issue occurred)"
                  value={pageUrl} onChange={e => setPageUrl(e.target.value)}
                  placeholder="https://app.example.com/..."
                  fullWidth size="small"
                />

                {/* Bug wizard: one-shot snapshot of the current page, attached. */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                  <Button
                    onClick={takeSnapshot}
                    disabled={snapping}
                    variant="outlined"
                    size="small"
                    startIcon={snapping ? <CircularProgress size={14} /> : <CameraAltIcon sx={{ fontSize: 16 }} />}
                    sx={{ borderRadius: '4px', textTransform: 'none', fontWeight: 600, borderColor: 'var(--rule-strong)', color: 'var(--ink)' }}
                  >
                    {snapping ? 'Capturing…' : 'Take snapshot of this page'}
                  </Button>
                  <Typography variant="caption" sx={{ color: 'var(--ink-faint)' }}>
                    Grabs a screenshot of what's behind this box and attaches it.
                  </Typography>
                </Box>
              </Box>
            </Collapse>

            {/* File upload area */}
            <Box
              sx={{
                border: '1.5px dashed var(--rule-strong)',
                borderRadius: '4px', p: 2, textAlign: 'center', cursor: 'pointer',
                transition: 'all 0.12s',
                '&:hover': { borderColor: 'var(--indigo)', bgcolor: 'var(--indigo-soft)' },
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 0.5 }}>
                <AttachFileIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Click to upload, drag & drop, or <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}><ContentPasteIcon sx={{ fontSize: 14 }} /> paste</Box> screenshots
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">Images, PDFs, up to 10MB each (max {MAX_FILES} files)</Typography>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,.pdf,.txt,.mp4,.webm"
                style={{ display: 'none' }}
                onChange={e => { if (e.target.files) { addFiles(e.target.files); e.target.value = ''; } }}
              />
            </Box>

            {/* Attachment previews */}
            {attachments.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {attachments.map((att, idx) => (
                  <Box key={idx} sx={{ position: 'relative', borderRadius: '4px', overflow: 'hidden', border: '1px solid var(--rule)' }}>
                    {att.preview ? (
                      <Box component="img" src={att.preview} alt={att.file.name}
                        sx={{ width: 80, height: 80, objectFit: 'cover', display: 'block' }} />
                    ) : (
                      <Box sx={{ width: 80, height: 80, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.hover', px: 0.5 }}>
                        <ImageIcon sx={{ fontSize: 20, color: 'text.secondary', mb: 0.25 }} />
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '10.5px', maxWidth: 72 }}>
                          {att.file.name}
                        </Typography>
                      </Box>
                    )}
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); removeAttachment(idx); }}
                      sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'var(--ink)', color: 'var(--paper)', width: 18, height: 18, '&:hover': { bgcolor: 'var(--ink-soft)' } }}
                    >
                      <CloseIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}

            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
          </Box>
        )}
      </DialogContent>

      {!success && (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} color="inherit" disabled={submitting}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '4px' }}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!description.trim() || submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : undefined}
            sx={{
              textTransform: 'none', fontWeight: 700, borderRadius: '4px', boxShadow: 'none',
              bgcolor: isBug ? 'var(--paprika)' : 'var(--indigo)',
              '&:hover': { bgcolor: isBug ? 'var(--paprika)' : 'var(--indigo)', boxShadow: 'none', filter: 'brightness(0.95)' },
            }}
          >
            {submitting ? 'Submitting...' : `Submit ${isBug ? 'Bug Report' : 'Feature Request'}`}
          </Button>
        </DialogActions>
      )}
      </HubThemeScope>
    </Dialog>
  );
};

export default FeatureRequestDialog;
